import Dexie, { Table } from 'dexie';

export interface CachedQueryRow {
    id?: number;
    queryKey: string;
    data: unknown;
    timestamp: number;
    expiresAt: number;
}

// Define types for our cached data
export type CachedSatelliteData = CachedQueryRow;

export type CachedWSAData = CachedQueryRow;

export type CachedAuroraReports = CachedQueryRow;

export type CachedUserData = CachedQueryRow;

export type CachedCloudReports = CachedQueryRow;

export type CachedDIndexModelArtifacts = CachedQueryRow;

export type CachedSpaceWeather = CachedQueryRow;

export type CachedPublicLocations = CachedQueryRow;

/** Persisted SDO HMI disc image so the UI can show the last frame while a new one loads. */
export interface CachedSdoHmiImage {
    id: string;
    blob: Blob;
    bucket: number;
    savedAt: number;
}

/**
 * Minimal shape of a queued offline report row. The full discriminated union
 * lives in offline-report-queue.ts (which validates rows on read); keeping the
 * Dexie type loose avoids a circular import between the two modules.
 */
export interface OfflineReportQueueRow {
    id: string;
    ownerUserId: string;
    createdAt: number;
    [key: string]: unknown;
}

/** Keep cached rows on disk for local-first reads; TTL only controls refetch. */
const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Dexie Database for Aurora Forecast App
 * Caches Convex query results in IndexedDB for instant loading
 */
export class AuroraForecastDB extends Dexie {
    satelliteData!: Table<CachedSatelliteData, number>;
    wsaData!: Table<CachedWSAData, number>;
    auroraReports!: Table<CachedAuroraReports, number>;
    userData!: Table<CachedUserData, number>;
    cloudReports!: Table<CachedCloudReports, number>;
    dIndexModelArtifacts!: Table<CachedDIndexModelArtifacts, number>;
    spaceWeather!: Table<CachedSpaceWeather, number>;
    publicLocations!: Table<CachedPublicLocations, number>;
    offlineReportQueue!: Table<OfflineReportQueueRow, string>;
    sdoHmiImage!: Table<CachedSdoHmiImage, string>;

    constructor() {
        super('AuroraForecastDB');
        
        this.version(1).stores({
            satelliteData: '++id, queryKey, timestamp, expiresAt',
            wsaData: '++id, queryKey, timestamp, expiresAt',
            auroraReports: '++id, queryKey, timestamp, expiresAt',
            userData: '++id, queryKey, timestamp, expiresAt',
        });
        this.version(2).stores({
            cloudReports: '++id, queryKey, timestamp, expiresAt',
        });
        this.version(3).stores({
            dIndexModelCoefficients: '++id, queryKey, timestamp, expiresAt',
        });
        this.version(4).stores({
            spaceWeather: '++id, queryKey, timestamp, expiresAt',
        });
        this.version(5).stores({
            dIndexModelArtifacts: '++id, queryKey, timestamp, expiresAt',
        });
        this.version(6).stores({
            publicLocations: '++id, queryKey, timestamp, expiresAt',
        });
        // v7: offline report queue moves here from localStorage (much larger
        // quota for queued images); drop the orphaned dIndexModelCoefficients
        // table left behind by the v5 rename to dIndexModelArtifacts.
        this.version(7).stores({
            offlineReportQueue: 'id, ownerUserId, createdAt',
            dIndexModelCoefficients: null,
        });
        // v8: persist last SDO HMI disc image for seamless refresh.
        this.version(8).stores({
            sdoHmiImage: 'id, savedAt',
        });
    }

    /**
     * Remove cache rows older than MAX_CACHE_AGE_MS (not TTL expiry).
     */
    async cleanupExpired(): Promise<number> {
        const cutoff = Date.now() - MAX_CACHE_AGE_MS;
        const [s, w, a, u, c, d, sw, p] = await Promise.all([
            this.satelliteData.where('timestamp').below(cutoff).delete(),
            this.wsaData.where('timestamp').below(cutoff).delete(),
            this.auroraReports.where('timestamp').below(cutoff).delete(),
            this.userData.where('timestamp').below(cutoff).delete(),
            this.cloudReports.where('timestamp').below(cutoff).delete(),
            this.dIndexModelArtifacts.where('timestamp').below(cutoff).delete(),
            this.spaceWeather.where('timestamp').below(cutoff).delete(),
            this.publicLocations.where('timestamp').below(cutoff).delete(),
        ]);
        return (s ?? 0) + (w ?? 0) + (a ?? 0) + (u ?? 0) + (c ?? 0) + (d ?? 0) + (sw ?? 0) + (p ?? 0);
    }

    /**
     * Get cached data by query key
     */
    async getCached<T>(table: Table<CachedQueryRow, number>, queryKey: string): Promise<T | null> {
        const result = await this.getCachedWithExpiry<T>(table, queryKey);
        return result?.data ?? null;
    }

    /**
     * Get cached data with expiry time. Serves stale rows for local-first UX.
     */
    async getCachedWithExpiry<T>(
        table: Table<CachedQueryRow, number>,
        queryKey: string
    ): Promise<{ data: T; expiresAt: number; isStale: boolean } | null> {
        const cached = await table.where('queryKey').equals(queryKey).first();

        if (!cached) return null;

        const isStale = cached.expiresAt < Date.now();
        return { data: cached.data as T, expiresAt: cached.expiresAt, isStale };
    }

    /**
     * Store data in cache
     */
    async setCached<T>(
        table: Table<CachedQueryRow, number>,
        queryKey: string,
        data: T,
        ttl: number = 5 * 60 * 1000 // Default 5 minutes
    ) {
        const now = Date.now();
        
        // Remove old cache for this query key
        await table.where('queryKey').equals(queryKey).delete();
        
        // Store new cache
        await table.add({
            queryKey,
            data,
            timestamp: now,
            expiresAt: now + ttl,
        });
    }
}

// Export singleton instance
export const db = new AuroraForecastDB();

// Clean up expired entries on startup and every 5 minutes (browser only)
if (typeof window !== "undefined" && typeof window.indexedDB !== "undefined") {
  db.cleanupExpired().catch((err) => console.warn("Dexie cleanup failed:", err));
  const CLEANUP_INTERVAL_MS = 5 * 60 * 1000;
  const cleanupIntervalId = setInterval(() => {
    db.cleanupExpired().catch((err) => console.warn("Dexie cleanup failed:", err));
  }, CLEANUP_INTERVAL_MS);
  if (typeof window.addEventListener === "function") {
    window.addEventListener("beforeunload", () => clearInterval(cleanupIntervalId));
  }
}
